/**
 * MANAGEMENT (14) · Subscriptions screen i18n (FR default + AR-RTL). ADDITIVE to the shell `../messages.ts`
 * (which already owns `mgmt.subscriptions.title` / `.subtitle` + the nav label) — this registers ONLY the
 * NEW keys the directory table + the plan-change modal need, so it never collides with the shell dictionary
 * and the shell file stays untouched. `registerMessages` is `Object.assign` (idempotent); the screen calls
 * `registerSubscriptionsMessages()` at module-eval (a `registered` latch makes it a no-op after the first).
 *
 * Codes / module SKUs / dates render inside `.nv-ltr-island` so figures read identically under dir="rtl".
 * A missing AR key falls back to the FR value, then the raw key (never a blank, never a crash).
 */
import { registerMessages } from "@/app";

const FR: Record<string, string> = {
  // ── directory ────────────────────────────────────────────────────────────────
  "mgmt.subs.search": "Rechercher un laboratoire (référence, raison sociale)…",
  "mgmt.subs.filter.statut": "Statut",
  "mgmt.subs.filter.statut.all": "Tous les statuts",
  "mgmt.subs.col.lab": "Laboratoire",
  "mgmt.subs.col.deploy": "Déploiement",
  "mgmt.subs.col.plan": "Plan",
  "mgmt.subs.col.statut": "Statut",
  "mgmt.subs.col.modules": "Modules",
  "mgmt.subs.col.projectedAt": "Mise à jour",
  "mgmt.subs.col.actions": "Actions",
  "mgmt.subs.row.notSynced": "Jamais synchronisé",
  "mgmt.subs.row.noPlan": "Aucun plan",
  "mgmt.subs.modules.count": "{cloud} cloud · {local} local",

  // ── insight strip (derived from the loaded directory — répartition + parc) ──────
  "mgmt.subs.insight.title": "Parc d'abonnements (page courante)",
  "mgmt.subs.insight.byStatut": "Par statut",
  "mgmt.subs.insight.byStatut.aria": "Répartition des abonnements par statut",
  "mgmt.subs.insight.empty": "Aucun abonnement à analyser sur cette page.",
  "mgmt.subs.insight.total": "Laboratoires",
  "mgmt.subs.insight.active": "Actifs (essai/actif/grâce)",
  "mgmt.subs.insight.unsynced": "Non synchronisés",
  "mgmt.subs.insight.cancelled": "Annulés",
  "mgmt.subs.action.change": "Changer le plan",
  "mgmt.subs.action.cancel": "Annuler l'abonnement",
  "mgmt.subs.empty.title": "Aucun abonnement",
  "mgmt.subs.empty.desc": "Aucun laboratoire ne correspond à ce filtre.",
  "mgmt.subs.empty.search": "Aucun laboratoire ne correspond à la recherche.",

  // ── statut labels (the central subscription vocabulary) ──────────────────────
  "mgmt.subs.statut.ESSAI": "Essai",
  "mgmt.subs.statut.ACTIVE": "Actif",
  "mgmt.subs.statut.PAST_DUE": "Impayé (grâce)",
  "mgmt.subs.statut.SUSPENDUE": "Suspendu",
  "mgmt.subs.statut.EXPIREE": "Expiré",
  "mgmt.subs.statut.ANNULEE": "Annulé",

  // ── change-plan modal ────────────────────────────────────────────────────────
  "mgmt.subs.modal.title": "Changer le plan — {lab}",
  "mgmt.subs.modal.plan": "Plan",
  "mgmt.subs.modal.plan.placeholder": "ex. PRO, STARTER",
  "mgmt.subs.modal.statut": "Statut",
  "mgmt.subs.modal.modulesCloud": "Modules cloud",
  "mgmt.subs.modal.modulesCloud.hint": "SKU séparés par une virgule (suspendus hors statut actif).",
  "mgmt.subs.modal.modulesLocal": "Modules locaux",
  "mgmt.subs.modal.modulesLocal.hint": "Bundles installés sur la boîte — conservés même après annulation.",
  "mgmt.subs.modal.reason": "Motif",
  "mgmt.subs.modal.reason.hint": "Pourquoi ce changement (tracé sur la projection).",
  "mgmt.subs.modal.submit": "Appliquer le changement",
  "mgmt.subs.modal.cancel": "Annuler",
  "mgmt.subs.modal.statutRequired": "Le statut est requis.",
  "mgmt.subs.saved": "Abonnement mis à jour.",

  // ── cancel modal ─────────────────────────────────────────────────────────────
  "mgmt.subs.cancel.title": "Annuler l'abonnement — {lab}",
  "mgmt.subs.cancel.warn":
    "Les modules cloud seront désactivés. Les bundles locaux du laboratoire sont conservés. L'abonnement peut être réémis ultérieurement.",
  "mgmt.subs.cancel.reason": "Motif d'annulation",
  "mgmt.subs.cancel.submit": "Confirmer l'annulation",
  "mgmt.subs.cancelled": "Abonnement annulé.",

  // ── generic ──────────────────────────────────────────────────────────────────
  "mgmt.subs.error": "Une erreur est survenue.",
};

const AR: Record<string, string> = {
  // ── directory ────────────────────────────────────────────────────────────────
  "mgmt.subs.search": "ابحث عن مختبر (المرجع، الاسم التجاري)…",
  "mgmt.subs.filter.statut": "الحالة",
  "mgmt.subs.filter.statut.all": "جميع الحالات",
  "mgmt.subs.col.lab": "المختبر",
  "mgmt.subs.col.deploy": "النشر",
  "mgmt.subs.col.plan": "الخطة",
  "mgmt.subs.col.statut": "الحالة",
  "mgmt.subs.col.modules": "الوحدات",
  "mgmt.subs.col.projectedAt": "آخر تحديث",
  "mgmt.subs.col.actions": "إجراءات",
  "mgmt.subs.row.notSynced": "لم تتم المزامنة بعد",
  "mgmt.subs.row.noPlan": "لا توجد خطة",
  "mgmt.subs.modules.count": "{cloud} سحابي · {local} محلي",

  "mgmt.subs.insight.title": "محفظة الاشتراكات (الصفحة الحالية)",
  "mgmt.subs.insight.byStatut": "حسب الحالة",
  "mgmt.subs.insight.byStatut.aria": "توزيع الاشتراكات حسب الحالة",
  "mgmt.subs.insight.empty": "لا توجد اشتراكات للتحليل في هذه الصفحة.",
  "mgmt.subs.insight.total": "المختبرات",
  "mgmt.subs.insight.active": "النشطة (تجريبي/نشط/سماح)",
  "mgmt.subs.insight.unsynced": "غير المتزامنة",
  "mgmt.subs.insight.cancelled": "الملغاة",
  "mgmt.subs.action.change": "تغيير الخطة",
  "mgmt.subs.action.cancel": "إلغاء الاشتراك",
  "mgmt.subs.empty.title": "لا توجد اشتراكات",
  "mgmt.subs.empty.desc": "لا يوجد مختبر يطابق هذا المرشّح.",
  "mgmt.subs.empty.search": "لا يوجد مختبر يطابق البحث.",

  // ── statut labels ────────────────────────────────────────────────────────────
  "mgmt.subs.statut.ESSAI": "تجريبي",
  "mgmt.subs.statut.ACTIVE": "نشط",
  "mgmt.subs.statut.PAST_DUE": "متأخر الدفع (سماح)",
  "mgmt.subs.statut.SUSPENDUE": "موقوف",
  "mgmt.subs.statut.EXPIREE": "منتهٍ",
  "mgmt.subs.statut.ANNULEE": "ملغى",

  // ── change-plan modal ────────────────────────────────────────────────────────
  "mgmt.subs.modal.title": "تغيير الخطة — {lab}",
  "mgmt.subs.modal.plan": "الخطة",
  "mgmt.subs.modal.plan.placeholder": "مثال: PRO، STARTER",
  "mgmt.subs.modal.statut": "الحالة",
  "mgmt.subs.modal.modulesCloud": "الوحدات السحابية",
  "mgmt.subs.modal.modulesCloud.hint": "وحدات SKU مفصولة بفواصل (تُعلَّق خارج الحالة النشطة).",
  "mgmt.subs.modal.modulesLocal": "الوحدات المحلية",
  "mgmt.subs.modal.modulesLocal.hint": "الحزم المثبّتة على الجهاز — تبقى حتى بعد الإلغاء.",
  "mgmt.subs.modal.reason": "السبب",
  "mgmt.subs.modal.reason.hint": "سبب هذا التغيير (يُسجَّل على الإسقاط).",
  "mgmt.subs.modal.submit": "تطبيق التغيير",
  "mgmt.subs.modal.cancel": "إلغاء",
  "mgmt.subs.modal.statutRequired": "الحالة مطلوبة.",
  "mgmt.subs.saved": "تم تحديث الاشتراك.",

  // ── cancel modal ─────────────────────────────────────────────────────────────
  "mgmt.subs.cancel.title": "إلغاء الاشتراك — {lab}",
  "mgmt.subs.cancel.warn":
    "سيتم تعطيل الوحدات السحابية. تبقى الحزم المحلية للمختبر. يمكن إعادة إصدار الاشتراك لاحقًا.",
  "mgmt.subs.cancel.reason": "سبب الإلغاء",
  "mgmt.subs.cancel.submit": "تأكيد الإلغاء",
  "mgmt.subs.cancelled": "تم إلغاء الاشتراك.",

  // ── generic ──────────────────────────────────────────────────────────────────
  "mgmt.subs.error": "حدث خطأ.",
};

let registered = false;
export function registerSubscriptionsMessages(): void {
  if (registered) return;
  registered = true;
  registerMessages("fr", FR);
  registerMessages("ar", AR);
}
